import { ExperienceOrder } from '@mocli/MocliCommonLib';

import { GameType } from 'src/app/services/ConfigurationManagerService';

export type OrderNavSectionKey = 'data' | 'configure' | 'configurator' | 'prize' | 'conditional' | 'socials' | 'stats';
export type OrderNavOptionKey = 'userForm' | 'prizeDraw' | 'eventsModal' | 'socials';
export type OrderNavStandalonePageKey = 'jsonEditor' | 'orderOptions' | 'statsExplorer' | 'overview';

export interface OrderNavSection {
  key: OrderNavSectionKey;
  label: string;
  basePath: string;
  optionKey?: OrderNavOptionKey;
}

export interface OrderNavRouteMatch {
  sectionKey: OrderNavSectionKey | null;
  orderId: number;
  /** Libellé affiché pour les pages hors-section (ex: éditeur JSON admin). */
  label?: string;
  standalonePageKey?: OrderNavStandalonePageKey;
}

export interface OrderNavMetadata {
  gameTypeLabel: string | null;
  tags: string[];
  enabledOptionalSectionKeys: OrderNavSectionKey[];
}

export const ORDER_NAV_SECTIONS: readonly OrderNavSection[] = [
  { key: 'configure', label: 'Paramètres', basePath: '/configure' },
  { key: 'configurator', label: 'Édition de contenu', basePath: '/configurator' },
  { key: 'stats', label: 'Statistiques', basePath: '/stats' },
  { key: 'data', label: 'Collecte de données', basePath: '/data', optionKey: 'userForm' },
  { key: 'prize', label: 'Système de gains', basePath: '/prize', optionKey: 'prizeDraw' },
  { key: 'socials', label: 'Engagement récompensé', basePath: '/socials', optionKey: 'socials' },
  { key: 'conditional', label: 'Pop-up conditionnelle', basePath: '/conditional', optionKey: 'eventsModal' }
] as const;

export const ALWAYS_VISIBLE_ORDER_NAV_SECTION_KEYS: readonly OrderNavSectionKey[] = ['configure', 'configurator', 'stats'];

export const ORDER_NAV_SECTION_BY_KEY: Readonly<Record<OrderNavSectionKey, OrderNavSection>> = {
  configure: ORDER_NAV_SECTIONS[0],
  configurator: ORDER_NAV_SECTIONS[1],
  stats: ORDER_NAV_SECTIONS[2],
  data: ORDER_NAV_SECTIONS[3],
  prize: ORDER_NAV_SECTIONS[4],
  socials: ORDER_NAV_SECTIONS[5],
  conditional: ORDER_NAV_SECTIONS[6]
};

export const GAME_TYPE_LABELS: Readonly<Record<GameType, string>> = {
  [GameType.CODEHUNT]: 'Chasse aux codes',
  [GameType.MULTI_GAMES]: 'Multi-jeux',
  [GameType.MULTI_QUIZ]: 'Multi-quiz',
  [GameType.TREASURE_HUNT]: 'Chasse au trésor',
  [GameType.RIDDLE_HUNT]: 'Chasse aux énigmes',
  [GameType.LANDING_PAGE]: 'Liste d\'expériences'
};

export function isOrderNavSectionKey(value: string): value is OrderNavSectionKey {
  return value in ORDER_NAV_SECTION_BY_KEY;
}

export function splitOrderTags(tags: string | null | undefined): string[] {
  return (tags ?? '')
    .split('|')
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);
}

export function resolveEnabledOptionalOrderNavSectionKeys(orderOptionsResponse: any): OrderNavSectionKey[] {
  const options = orderOptionsResponse?.options;
  if (!options || typeof options !== 'object') {
    return [];
  }

  return ORDER_NAV_SECTIONS
    .filter((section) => section.optionKey && isAdditionalOrderOptionEnabled(options, section.optionKey))
    .map((section) => section.key);
}

export function resolveVisibleOrderNavSectionKeys(
  enabledOptionalSectionKeys: readonly OrderNavSectionKey[],
  currentSectionKey?: OrderNavSectionKey | null
): OrderNavSectionKey[] {
  const enabledOptionalKeys = new Set(enabledOptionalSectionKeys);

  return ORDER_NAV_SECTIONS
    .filter((section) => ALWAYS_VISIBLE_ORDER_NAV_SECTION_KEYS.includes(section.key)
      || enabledOptionalKeys.has(section.key)
      || section.key === currentSectionKey)
    .map((section) => section.key);
}

function isAdditionalOrderOptionEnabled(options: Record<string, any>, optionKey: OrderNavOptionKey): boolean {
  if (!Object.prototype.hasOwnProperty.call(options, optionKey)) {
    return false;
  }

  const optionValue = options[optionKey];
  if (optionValue && typeof optionValue === 'object' && 'isPresent' in optionValue) {
    return optionValue.isPresent === true;
  }

  return optionValue !== null && optionValue !== undefined;
}

export function resolveGameTypeLabelFromConfiguration(
  orderConfiguration: any,
  orderDetails?: ExperienceOrder | null
): string | null {
  const configuration = orderConfiguration?.configuration;
  if (!configuration) {
    return orderDetails?.experienceInfo?.name ?? null;
  }

  const modularRootContent = configuration.ModularRoot?.content;
  if (Array.isArray(modularRootContent) && modularRootContent.length > 0) {
    const hasLandingPage = modularRootContent.some((contentItem: any) => contentItem?.LandingPage);
    if (hasLandingPage) {
      return GAME_TYPE_LABELS[GameType.LANDING_PAGE];
    }

    const codeHuntContent = modularRootContent.find((contentItem: any) => contentItem?.CodeHunt)?.CodeHunt
      ?? modularRootContent[modularRootContent.length - 1]?.CodeHunt;

    if (codeHuntContent?.isMultiGames) {
      return GAME_TYPE_LABELS[GameType.MULTI_GAMES];
    }

    if (codeHuntContent?.isQuizLeaderboard) {
      return GAME_TYPE_LABELS[GameType.MULTI_QUIZ];
    }

    if (codeHuntContent?.isTreasureHunt) {
      return GAME_TYPE_LABELS[GameType.TREASURE_HUNT];
    }

    if (codeHuntContent?.isRiddleHunt) {
      return GAME_TYPE_LABELS[GameType.RIDDLE_HUNT];
    }

    if (codeHuntContent) {
      return GAME_TYPE_LABELS[GameType.CODEHUNT];
    }

    return GAME_TYPE_LABELS[GameType.MULTI_GAMES];
  }

  const codeHuntRoot = configuration.CodeHuntRoot;
  if (codeHuntRoot) {
    if (codeHuntRoot?.isMultiGames) {
      return GAME_TYPE_LABELS[GameType.MULTI_GAMES];
    }

    if (codeHuntRoot?.isQuizLeaderboard) {
      return GAME_TYPE_LABELS[GameType.MULTI_QUIZ];
    }

    if (codeHuntRoot?.isTreasureHunt) {
      return GAME_TYPE_LABELS[GameType.TREASURE_HUNT];
    }

    if (codeHuntRoot?.isRiddleHunt) {
      return GAME_TYPE_LABELS[GameType.RIDDLE_HUNT];
    }

    return GAME_TYPE_LABELS[GameType.CODEHUNT];
  }

  const multiPageRoot = configuration.MultiPageRoot;
  if (multiPageRoot) {
    const multiPageContent = Array.isArray(multiPageRoot.content) ? multiPageRoot.content : [];
    if (multiPageContent.some((contentItem: any) => contentItem?.SimpleQuiz)) {
      return GAME_TYPE_LABELS[GameType.MULTI_QUIZ];
    }

    return GAME_TYPE_LABELS[GameType.MULTI_GAMES];
  }

  if (configuration.UniqueGameRoot) {
    return GAME_TYPE_LABELS[GameType.CODEHUNT];
  }

  return orderDetails?.experienceInfo?.name ?? null;
}